import { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, InputNumber, Select, Table, Row, Col, message, Button, Card, Popconfirm } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { getData, postData, putData } from "../../../apiServices/common";
import {
  PACKING_OPERATOR_MAPPING_ENDPOINT,
  PACKING_OPERATOR_MAPPING_ADD_ENDPOINT,
  PACKING_OPERATOR_MAPPING_EDIT_ENDPOINT,
  PACKING_LINES_ENDPOINT,
  PACKING_TABLE_ENDPOINT,
  GET_DIGITALIZATION_APP_USERS,
} from "../../../apiServices/API_ENDPOINTS";

const { Option } = Select;

const PackingOperatorMappingForm = forwardRef((props, ref) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingPackingTables, setLoadingPackingTables] = useState(false);
  const [operators, setOperators] = useState([]);
  const [packingLinesList, setPackingLinesList] = useState([]);
  const [packingTablesList, setPackingTablesList] = useState([]);
  const [operatorsList, setOperatorsList] = useState([]);
  const [selectedPackingLineId, setSelectedPackingLineId] = useState(null);

  // Fetch packing lines
  const getPackingLines = useCallback(async (searchValue = null) => {
    const query = `${PACKING_LINES_ENDPOINT}`;
    const bodyData = {
      ...(searchValue && { name: searchValue }),
      page: 1,
      per_page: 100,
    };

    const response = await getData(query, false, bodyData);
    if (response?.status === 200 || response?.code === 200) {
      const data = response?.data?.data?.data || response?.data?.data || [];
      setPackingLinesList(data);
    }
  }, []);

  // Fetch packing tables based on packing line
  const getPackingTables = useCallback(async (packingLineId, searchValue = null) => {
    if (!packingLineId) {
      setPackingTablesList([]);
      setLoadingPackingTables(false);
      return;
    }

    setLoadingPackingTables(true);
    const query = `${PACKING_TABLE_ENDPOINT}`;
    const bodyData = {
      packing_line_id: packingLineId,
      ...(searchValue && { name: searchValue }),
      page: 1,
      per_page: 100,
    };

    try {
      const response = await getData(query, false, bodyData);
      if (response?.status === 200 || response?.code === 200) {
        const data = response?.data?.data?.data || response?.data?.data || [];
        setPackingTablesList(data);
      }
    } catch (error) {
      console.error("Error fetching packing tables:", error);
    } finally {
      setLoadingPackingTables(false);
    }
  }, []);

  // Fetch operators
  const getOperators = useCallback(async (searchValue = null) => {
    const query = `${GET_DIGITALIZATION_APP_USERS}`;
    const bodyData = {
      ...(searchValue && { id_card_no: searchValue }),
      page: 1,
      per_page: 100,
      role_id: 58
    };

    const response = await getData(query, false, bodyData);
    if (response?.data?.code === 200) {
      const data = response?.data?.data?.data || [];
      setOperatorsList(data);
    }
  }, []);

  // Load initial data
  useEffect(() => {
    getPackingLines();
    getOperators();
    // Don't fetch packing tables initially - wait for packing line selection
  }, [getPackingLines, getOperators]);

  // Load edit data
  useEffect(() => {
    if (isEdit && id) {
      setLoading(true);
      getData(`${PACKING_OPERATOR_MAPPING_ENDPOINT}/${id}/edit`)
        .then((response) => {
          if (response?.data?.code === 200) {
            const data = response?.data?.data;

            // Check and add packing line if not in list
            if (data?.packing_line && data?.packing_line?.id) {
              setPackingLinesList((prevList) => {
                const exists = prevList.some((line) => line.id === data.packing_line.id);
                if (!exists) {
                  return [
                    // Put the full object from single GET response at the top
                    data.packing_line,
                    ...prevList,
                  ];
                }
                return prevList;
              });
            }

            // Check and add operators and tables if not in list
            if (data?.operators && Array.isArray(data.operators)) {
              data.operators.forEach((operator) => {
                if (operator?.operator && operator?.operator?.id) {
                  setOperatorsList((prevList) => {
                    const exists = prevList.some((op) => op.id === operator.operator.id);
                    if (!exists) {
                      return [
                        // Use full operator object so dropdown has all fields (e.g. additional_info.id_card_no)
                        operator.operator,
                        ...prevList,
                      ];
                    }
                    return prevList;
                  });
                }

                if (operator?.packing_table && operator?.packing_table?.id) {
                  setPackingTablesList((prevList) => {
                    const exists = prevList.some((table) => table.id === operator.packing_table.id);
                    if (!exists) {
                      return [
                        // Use full packing_table object from single GET response
                        operator.packing_table,
                        ...prevList,
                      ];
                    }
                    return prevList;
                  });
                }
              });
            }

            // Set form values
            form.setFieldsValue({
              packing_line_id: data?.packing_line_id,
              total_hourly_target: data?.total_hourly_target,
            });

            // Set selected packing line and fetch packing tables
            if (data?.packing_line_id) {
              setSelectedPackingLineId(data.packing_line_id);
              getPackingTables(data.packing_line_id);
            }

            // Set operators table data
            const operatorsData = (data?.operators || []).map((op, index) => ({
              key: `operator-${index}`,
              id: op.id || 0,
              operator_id: op.operator_id,
              packing_table_id: op.packing_table_id,
              hourly_target: op.hourly_target,
              is_delete: 0,
            }));
            setOperators(operatorsData);
            setLoading(false);
          } else {
            setLoading(false);
            message.error("Error fetching data");
          }
        })
        .catch((error) => {
          setLoading(false);
          message.error("Error fetching data");
        });
    }
  }, [isEdit, id, form, getPackingTables]);

  // Watch for packing line changes
  const packingLineId = Form.useWatch("packing_line_id", form);

  useEffect(() => {
    if (packingLineId && packingLineId !== selectedPackingLineId) {
      setSelectedPackingLineId(packingLineId);
      // Fetch packing tables for the selected packing line
      getPackingTables(packingLineId);
      // Clear packing table selections in existing operators when packing line changes
      setOperators((prev) =>
        prev.map((op) => ({
          ...op,
          packing_table_id: null,
        })),
      );
    } else if (!packingLineId) {
      setSelectedPackingLineId(null);
      setPackingTablesList([]);
      // Clear packing table selections when packing line is cleared
      setOperators((prev) =>
        prev.map((op) => ({
          ...op,
          packing_table_id: null,
        })),
      );
    }
  }, [packingLineId, selectedPackingLineId, getPackingTables]);

  // Calculate total hourly target
  useEffect(() => {
    const total = operators.filter((op) => !op.is_delete).reduce((sum, op) => sum + (op.hourly_target || 0), 0);
    form.setFieldsValue({ total_hourly_target: total });
  }, [operators, form]);

  const addOperatorRow = () => {
    const newRow = {
      key: `operator-${Date.now()}`,
      id: 0,
      operator_id: null,
      packing_table_id: null,
      hourly_target: 0,
      is_delete: 0,
    };
    setOperators([...operators, newRow]);
  };

  const deleteOperatorRow = (key) => {
    setOperators((prev) =>
      prev
        .map((op) => {
          if (op.key === key) {
            if (isEdit && op.id > 0) {
              return { ...op, is_delete: 1 };
            }
            return null;
          }
          return op;
        })
        .filter((op) => op !== null),
    );
  };

  const updateOperatorField = (key, field, value) => {
    setOperators((prev) => prev.map((op) => (op.key === key ? { ...op, [field]: value } : op)));
  };

  const onFinish = async (values) => {
    setLoading(true);

    const operatorsPayload = operators
      .filter((op) => !op.is_delete)
      .map((op) => ({
        id: op.id || 0,
        operator_id: op.operator_id,
        packing_table_id: op.packing_table_id,
        hourly_target: op.hourly_target || 0,
        is_delete: 0,
      }));

    // Add deleted operators in edit mode
    if (isEdit) {
      operators
        .filter((op) => op.is_delete && op.id > 0)
        .forEach((op) => {
          operatorsPayload.push({
            id: op.id,
            operator_id: op.operator_id,
            packing_table_id: op.packing_table_id,
            hourly_target: op.hourly_target,
            is_delete: 1,
          });
        });
    }

    const bodyData = {
      packing_line_id: values.packing_line_id,
      total_hourly_target: values.total_hourly_target,
      operators: operatorsPayload,
    };

    try {
      let response;
      if (isEdit) {
        response = await putData(`${PACKING_OPERATOR_MAPPING_EDIT_ENDPOINT}/${id}`, bodyData);
      } else {
        response = await postData(PACKING_OPERATOR_MAPPING_ADD_ENDPOINT, bodyData);
      }

      if (response && response?.code === 200) {
        setLoading(false);
        message.success(
          isEdit ? "Packer Man Mapping Updated Successfully" : "Packer Man Mapping Created Successfully",
        );
        navigate(-1);
      } else {
        setLoading(false);
        message.error("Something went wrong");
      }
    } catch (error) {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  // Expose submit method and loading state via ref
  useImperativeHandle(ref, () => ({
    submit: () => {
      form.submit();
    },
    loading: loading,
  }));

  const operatorColumns = [
    {
      title: "Packer Man",
      dataIndex: "operator_id",
      key: "operator_id",
      width: 300,
      render: (_, record) => (
        <Select
          showSearch
          allowClear
          placeholder="Select Operator"
          value={record.operator_id}
          onSearch={(value) => getOperators(value)}
          onClear={() => getOperators()}
          optionFilterProp="children"
          filterOption={false}
          style={{ width: "100%", minWidth: 200 }}
          onChange={(value) => updateOperatorField(record.key, "operator_id", value)}
          dropdownMatchSelectWidth={false}
          dropdownStyle={{ minWidth: 300 }}
        >
          {operatorsList.map((operator) => (
            <Option key={operator.id} value={operator.id}>
              {operator.name} ({operator?.additional_info?.id_card_no})
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Packing Table",
      dataIndex: "packing_table_id",
      key: "packing_table_id",
      width: 250,
      render: (_, record) => (
        <Select
          showSearch
          allowClear
          placeholder={
            loadingPackingTables
              ? "Loading..."
              : selectedPackingLineId
              ? "Select Packing Table"
              : "Please select Packing Line first"
          }
          value={record.packing_table_id}
          disabled={!selectedPackingLineId || loadingPackingTables}
          loading={loadingPackingTables}
          onSearch={(value) => {
            if (selectedPackingLineId && !loadingPackingTables) {
              getPackingTables(selectedPackingLineId, value);
            }
          }}
          onClear={() => {
            if (selectedPackingLineId && !loadingPackingTables) {
              getPackingTables(selectedPackingLineId);
            }
          }}
          optionFilterProp="children"
          filterOption={false}
          style={{ width: "100%", minWidth: 200 }}
          onChange={(value) => updateOperatorField(record.key, "packing_table_id", value)}
        >
          {packingTablesList.map((table) => (
            <Option key={table.id} value={table.id}>
              {table.name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Hourly Target",
      dataIndex: "hourly_target",
      key: "hourly_target",
      width: 150,
      render: (_, record) => (
        <InputNumber
          min={0}
          style={{ width: "100%" }}
          value={record.hourly_target}
          onChange={(value) => updateOperatorField(record.key, "hourly_target", value || 0)}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title="Are you sure you want to delete this operator?"
          onConfirm={() => deleteOperatorRow(record.key)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="link" danger icon={<DeleteOutlined />}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ marginTop: "10px" }}>
      <Card>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="packing_line_id"
                label="Packing Line"
                rules={[{ required: true, message: "Packing Line is required" }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Packing Line"
                  onSearch={(value) => getPackingLines(value)}
                  onClear={() => getPackingLines()}
                  optionFilterProp="children"
                  filterOption={false}
                >
                  {packingLinesList.map((line) => (
                    <Option key={line.id} value={line.id}>
                      {line.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="total_hourly_target"
                label="Total Hourly Target"
                rules={[{ required: true, message: "Total Hourly Target is required" }]}
              >
                <InputNumber min={0} style={{ width: "100%" }} disabled />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
              <Button type="dashed" icon={<PlusOutlined />} onClick={addOperatorRow} disabled={!selectedPackingLineId}>
                Add Operator
              </Button>
            </div>

            <Table
              columns={operatorColumns}
              dataSource={operators.filter((op) => !op.is_delete)}
              pagination={false}
              rowKey="key"
              size="small"
              scroll={{ x: "max-content" }}
            />
          </div>
        </Form>
      </Card>
    </div>
  );
});

PackingOperatorMappingForm.displayName = "PackingOperatorMappingForm";

export default PackingOperatorMappingForm;
